if (!customElements.get('variants-collection')) {
  class VariantsCollection extends HTMLElement {
    /**
     * Properties.
     */
    selectors = {
      colorsSection: '[data-variants-color-section]',
      compareSection: '[data-variants-compare-section]',
      colorGroup: '[data-variants-color-group]',
      colorBtn: '[data-add-more-btn]',
      compareBtn: '[data-compare-btn]',
      filterCheckbox: '[data-variants-filter-checkbox]',
      filterApplyBtn: '[data-variants-apply-color-filters]',
      clearFilters: '[data-variants-clear-filters]',
      compareCheckbox: '[data-variants-compare-checkbox]',
      compareContainer: '[data-variants-compare-container]',
      compareMeta: '[data-variants-compare-meta]',
      productVariant: 'product-variant',
      dropdown: 'variants-dropdown',
      countLabel: '[data-count-label]',
      meta: '[data-meta]',
      hidden: '.hidden',
    };

    classes = {
      opened: 'is-opened',
    };

    attrs = {
      clearFilters: 'data-variants-clear-filters',
    };

    state = 'color-groups';
    compareItems = [];

    /**
     * constructor.
     */
    constructor() {
      super();
    }

    /**
     * connectedCallback.
     */
    connectedCallback() {
      this.colorsSection = this.querySelector(this.selectors.colorsSection);
      this.compareSection = this.querySelector(this.selectors.compareSection);
      this.colorGroups = this.querySelectorAll(this.selectors.colorGroup);
      this.compareBtns = this.querySelectorAll(this.selectors.compareBtn);
      this.compareContainer = this.querySelector(
        this.selectors.compareContainer,
      );
      this.compareMeta = this.querySelector(this.selectors.compareMeta);
      this.colorBtn = this.querySelector(this.selectors.colorBtn);
      this.maxCompareCount = parseInt(this.dataset.maxCompareCount || 0);
      this.filterCheckboxes = this.querySelectorAll(
        this.selectors.filterCheckbox,
      );
      this.filterApplyBtn = this.querySelector(this.selectors.filterApplyBtn);
      this.clearFilters = this.querySelector(this.selectors.clearFilters);
      this.dropdown = this.querySelector(this.selectors.dropdown);

      this.attachListeners();
    }

    /**
     * attachListeners.
     */
    attachListeners() {
      this.compareBtns.forEach((btn) => {
        btn.addEventListener('click', this.showCompareSection.bind(this));
      });

      this.colorBtn.addEventListener(
        'click',
        this.showColorsSection.bind(this),
      );

      this.addEventListener('compare', this.handleCompare.bind(this));
      this.addEventListener('remove', this.removeItem.bind(this));
      this.addEventListener('filter-colors', () => {
        if (this.state.toLowerCase() === 'color-groups') {
          this.filterColorGroups.call(this);
        } else if (this.state.toLowerCase() === 'compare-items') {
          this.filterCompareItems.call(this);
        }
      });
      this.addEventListener('reset-filters', this.resetFilters.bind(this));
      window.addEventListener('load', () => {
        this.handleInitialLoadCompare();
      });

      const firstButton = this.compareBtns[0];
      const top = firstButton.offsetTop;
      window.addEventListener('scroll', () => {
        firstButton.classList.toggle('is-sticky', window.scrollY >= top);
      });
    }

    /**
     * getFilterCheckboxes.
     * @returns {HTMLInputElement[]} - array of filter checkboxes
     */

    getFilterCheckboxes() {
      const checkboxesArray = [...this.filterCheckboxes, this.clearFilters];
      return checkboxesArray;
    }

    /**
     * resetCheckboxesCount.
     */
    resetCheckboxesCount() {
      this.getFilterCheckboxes().forEach((checkbox) => {
        const { count } = checkbox.dataset;

        checkbox.parentNode.classList.remove('hidden');
        checkbox.parentNode.querySelector(
          this.selectors.countLabel,
        ).textContent = `(${count})`;
      });
    }

    /**
     * setCompareCheckboxesCount.
     */
    setCompareCheckboxesCount() {
      // Reset checkboxes' count
      this.getFilterCheckboxes().forEach((checkbox) => {
        checkbox.setAttribute('data-compare-count', 0);
      });

      const allItems = [
        ...this.compareSection.querySelectorAll(this.selectors.productVariant),
      ];

      // Set new checkboxes' count
      allItems.forEach((item) => {
        const { groupId } = item.dataset;
        const checkboxSelector = this.selectors.filterCheckbox.concat(
          `#${groupId}`,
        );
        const filterCheckbox = this.querySelector(checkboxSelector);
        const { compareCount } = filterCheckbox.dataset;
        const updatedCompareCount = parseInt(compareCount) + 1;

        filterCheckbox.setAttribute('data-compare-count', updatedCompareCount);
      });

      // Update checkboxes count
      this.getFilterCheckboxes().forEach((checkbox) => {
        const { compareCount } = checkbox.dataset;
        const isClearCheckbox = checkbox.hasAttribute(this.attrs.clearFilters);

        if (isClearCheckbox) {
          checkbox.parentNode.querySelector(
            this.selectors.countLabel,
          ).textContent = `(${allItems.length})`;
        } else {
          if (parseInt(compareCount) === 0) {
            checkbox.parentNode.classList.add('hidden');
          } else {
            checkbox.parentNode.querySelector(
              this.selectors.countLabel,
            ).textContent = `(${compareCount})`;
          }
        }
      });
    }

    /**
     * showColorsSection.
     */
    showColorsSection() {
      this.state = 'color-groups';
      this.dropdown.handleClearFilters();
      this.colorsSection.classList.remove('hidden');
      this.compareSection.classList.add('hidden');
      this.toggleCompareBtns();
      this.resetCheckboxesCount();
      this.clearFilters.checked = true;
      this.colorGroups.forEach((group) => {
        group.classList.remove(this.classes.opened);
        group.hideElements(true);
        group.toggleBtn(false);
        group.renderMeta();
      });
    }

    /**
     * showCompareSection.
     */
    showCompareSection() {
      this.state = 'compare-items';
      this.dropdown.handleClearFilters();
      this.renderElements();
      this.colorsSection.classList.add('hidden');
      this.compareSection.classList.remove('hidden');
      this.compareSection.renderMeta();
      this.setCompareCheckboxesCount();
      this.clearFilters.checked = true;

      this.compareSection.scrollIntoView({
        behavior: 'smooth',
      });
    }

    /**
     * toggleCompareBtns.
     */
    toggleCompareBtns() {
      this.compareBtns.forEach((btn) => {
        btn.classList.add('hidden');
        btn.toggleAttribute('disabled', this.compareItems.length < 1);
      });

      const firstGroup = this.colorsSection.querySelector(
        `${this.selectors.colorGroup}:not(.hidden)`,
      );

      firstGroup
        .querySelector(this.selectors.compareBtn)
        .classList.remove('hidden');
    }

    /**
     * resetFilters.
     * @param {CustomEvent} e - reset-filters event from filter dropdown.
     */
    resetFilters(e) {
      this.colorGroups.forEach((group) => {
        group.classList.remove('hidden');
      });

      this.toggleCompareBtns();

      const compareItems = this.compareSection.querySelectorAll(
        this.selectors.productVariant,
      );

      compareItems.forEach((item) => {
        item.classList.remove('hidden');
      });

      this.renderCompareMeta(compareItems.length);
      this.renderCompareBtn(compareItems.length);
    }

    /**
     * filterColorGroups.
     * @param {CustomEvent} e - filter-colors event from filter dropdown.
     */
    filterColorGroups(e) {
      this.colorGroups.forEach((group) => {
        const { id: groupId } = group.dataset;
        const checkboxSelector = this.selectors.filterCheckbox.concat(
          `#${groupId}`,
        );
        const filterCheckbox = this.querySelector(checkboxSelector);

        group.classList.toggle('hidden', !filterCheckbox.checked);
        group.hideElements();
        group.toggleBtn(false);
      });

      this.toggleCompareBtns();
    }

    /**
     * filterCompareItems.
     * @param {CustomEvent} e - filter-colors event from filter dropdown.
     */
    filterCompareItems(e) {
      if (this.clearFilters.checked) {
        this.resetFilters();
      } else {
        const items = this.compareSection.querySelectorAll(
          this.selectors.productVariant,
        );

        const filteredItems = Array.from(items).filter((item) => {
          const { groupId } = item.dataset;
          const checkboxSelector = this.selectors.filterCheckbox.concat(
            `#${groupId}`,
          );
          const filterCheckbox = this.querySelector(checkboxSelector);

          item.classList.toggle('hidden', !filterCheckbox.checked);

          return filterCheckbox.checked;
        });

        this.renderCompareMeta(filteredItems.length);
        this.renderCompareBtn(filteredItems.length);
      }
    }

    /**
     * handleCompare.
     * @param {CustomEvent} e - compare event from products' checkbox.
     */
    handleCompare(e) {
      const { product, checked } = e.detail;

      if (checked) {
        this.compareItems.push(product);
      } else {
        this.compareItems = this.compareItems.filter(
          (item) => item.dataset.id !== product.dataset.id,
        );
      }

      this.toggleCompareBtns();
    }

    /**
     * handleInitialLoadCompare.
     */
    handleInitialLoadCompare() {
      const items = this.querySelectorAll(this.selectors.productVariant);
      items.forEach((item) => {
        const checkbox = item.querySelector(this.selectors.compareCheckbox);

        if (checkbox.checked) {
          this.compareItems.push(item);
        }
      });

      this.toggleCompareBtns();
    }

    /**
     * removeItem.
     * @param {CustomEvent} e - remove event from products' remove btn
     */
    removeItem(e) {
      const { productId } = e.detail;

      this.compareItems = this.compareItems.filter((item) => {
        if (item.dataset.id === productId) {
          const compareCheckbox = item.querySelector(
            this.selectors.compareCheckbox,
          );
          compareCheckbox.checked = false;
        }

        return item.dataset.id !== productId;
      });

      this.renderElements();
      this.renderCompareBtn();
      this.setCompareCheckboxesCount();
      this.filterCompareItems();
    }

    /**
     * getCorrespondingFilterCheckbox.
     * @param {string} id
     */
    getCorrespondingFilterCheckbox(id) {
      const checkboxSelector = this.selectors.filterCheckbox.concat(`#${id}`);
      const filterCheckbox = this.querySelector(checkboxSelector);

      return filterCheckbox;
    }

    /**
     * renderCompareMeta.
     */
    renderCompareMeta(currentItemCount = this.compareItems.length) {
      this.compareMeta.innerHTML = '';
      const isSectionOpened = this.compareSection.classList.contains(
        this.classes.opened,
      );

      const totalCount = currentItemCount;
      const difference = this.maxCompareCount - totalCount;
      let currentCount = 0;

      if (isSectionOpened) {
        this.compareMeta.innerHTML = `Showing ${totalCount} of ${totalCount}`;
        return;
      }

      if (difference > 0) {
        currentCount = totalCount;
      } else {
        currentCount = this.maxCompareCount;
      }

      this.compareMeta.innerHTML = `Showing ${currentCount} of ${totalCount}`;
    }

    /**
     * renderCompareBtn.
     */
    renderCompareBtn(currentItemCount = this.compareItems.length) {
      const totalCount = currentItemCount;
      const difference = this.maxCompareCount - totalCount;

      this.compareSection.toggleBtn(difference >= 0);
    }

    /**
     * renderElements.
     */
    renderElements() {
      this.compareContainer.innerHTML = '';

      this.compareItems.forEach((item, index) => {
        const clone = item.cloneNode(true);

        clone.classList.remove('hidden');
        clone.classList.add('product-variant--comparable');

        if (
          index + 1 > this.maxCompareCount &&
          !this.compareSection.classList.contains(this.classes.opened)
        ) {
          clone.classList.add('hidden');
        }

        this.compareContainer.appendChild(clone);
      });

      this.compareSection.renderMeta();
    }
  }

  customElements.define('variants-collection', VariantsCollection);
}
